import { action } from 'typesafe-actions';
import {
  BelActionTypes,
  BelShapeHandling,
  BelJobStatus,
  BelEnquiryDetails,
  BelValidationSummary,
  BelGeometryViolation,
  StripePrice,
  BelPollMode
} from './types';
import { BulkEnquiryLodgementResponse } from 'services/bulkEnquiryLodgement.service';

export const initiateBelStart = (body: any) =>
  action(BelActionTypes.INITIATE_BEL_START, body);
export const initiateBelSuccess = (payload: {
  belId: number;
  jobId: number;
  stripePrice: StripePrice;
}) => action(BelActionTypes.INITIATE_BEL_SUCCESS, payload);
export const initiateBelFailure = (error: string) =>
  action(BelActionTypes.INITIATE_BEL_FAILURE, error);

export const uploadFileStart = (payload: { file: File }) =>
  action(BelActionTypes.UPLOAD_FILE_START, payload);
export const uploadFileSuccess = (payload: {
  fileId: number;
  fileName: string;
  fileSize: string;
}) => action(BelActionTypes.UPLOAD_FILE_SUCCESS, payload);
export const uploadFileFailure = (error: string) =>
  action(BelActionTypes.UPLOAD_FILE_FAILURE, error);

export const transformFileStart = (fileId: number) =>
  action(BelActionTypes.TRANSFORM_FILE_START, fileId);
export const transformFileSuccess = (payload: {
  bulkId: number;
  jobId: number;
}) => action(BelActionTypes.TRANSFORM_FILE_SUCCESS, payload);
export const transformFileFailure = (error: string) =>
  action(BelActionTypes.TRANSFORM_FILE_FAILURE, error);

export const validateFileStart = (fileId: number) =>
  action(BelActionTypes.VALIDATE_FILE_START, fileId);
export const validateFileSuccess = (payload: {
  polygonCount: number;
  isValid: boolean;
}) => action(BelActionTypes.VALIDATE_FILE_SUCCESS, payload);
export const validateFileFailure = (error: string) =>
  action(BelActionTypes.VALIDATE_FILE_FAILURE, error);

export const pollJobStart = (payload: {
  jobId: number;
  pollMode: BelPollMode;
}) => action(BelActionTypes.POLL_JOB_START, payload);
export const pollJobUpdate = (status: BelJobStatus) =>
  action(BelActionTypes.POLL_JOB_UPDATE, status);
export const pollJobComplete = (payload: { enquiriesLodged: number }) =>
  action(BelActionTypes.POLL_JOB_COMPLETE, payload);
export const pollJobFailure = (error: string) =>
  action(BelActionTypes.POLL_JOB_FAILURE, error);
export const pollJobTimeout = (error: string) =>
  action(BelActionTypes.POLL_JOB_TIMEOUT, error);

export const fetchBelSuccess = (payload: BulkEnquiryLodgementResponse) =>
  action(BelActionTypes.FETCH_BEL_SUCCESS, payload);
export const fetchBelFailure = (error: string) =>
  action(BelActionTypes.FETCH_BEL_FAILURE, error);

export const fetchLivePriceStart = () =>
  action(BelActionTypes.FETCH_LIVE_PRICE_START);
export const fetchLivePriceSuccess = (price: StripePrice) =>
  action(BelActionTypes.FETCH_LIVE_PRICE_SUCCESS, price);
export const fetchLivePriceFailure = (error: string) =>
  action(BelActionTypes.FETCH_LIVE_PRICE_FAILURE, error);

export const submitBelDetailsStart = () =>
  action(BelActionTypes.SUBMIT_BEL_DETAILS_START);
export const submitBelDetailsSuccess = () =>
  action(BelActionTypes.SUBMIT_BEL_DETAILS_SUCCESS);
export const submitBelDetailsFailure = (error: string) =>
  action(BelActionTypes.SUBMIT_BEL_DETAILS_FAILURE, error);

export const fetchDownloadStart = (fileId: number) =>
  action(BelActionTypes.FETCH_DOWNLOAD_START, fileId);
export const fetchDownloadSuccess = (payload: { url: string }) =>
  action(BelActionTypes.FETCH_DOWNLOAD_SUCCESS, payload);
export const fetchDownloadFailure = (error: string) =>
  action(BelActionTypes.FETCH_DOWNLOAD_FAILURE, error);

export const createBelCheckoutStart = () =>
  action(BelActionTypes.CREATE_BEL_CHECKOUT_START);
export const createBelCheckoutSuccess = (payload: {
  clientSecret?: string;
  invoiceId?: string;
  completed?: boolean;
}) => action(BelActionTypes.CREATE_BEL_CHECKOUT_SUCCESS, payload);
export const createBelCheckoutFailure = (error: string) =>
  action(BelActionTypes.CREATE_BEL_CHECKOUT_FAILURE, error);
export const confirmBelPayment = () =>
  action(BelActionTypes.CONFIRM_BEL_PAYMENT);
export const verifyBelPayment = (sessionId: string) =>
  action(BelActionTypes.VERIFY_BEL_PAYMENT, { sessionId });
export const verifyBelPaymentSuccess = (payload: {
  paymentIntentId?: string | null;
}) => action(BelActionTypes.VERIFY_BEL_PAYMENT_SUCCESS, payload);

export const resetBelPayment = () => action(BelActionTypes.RESET_BEL_PAYMENT);

export const setBelStep = (step: number) =>
  action(BelActionTypes.SET_STEP, step);
export const setShapeHandling = (handling: BelShapeHandling) =>
  action(BelActionTypes.SET_SHAPE_HANDLING, handling);
export const setEnquiryDetails = (details: BelEnquiryDetails) =>
  action(BelActionTypes.SET_ENQUIRY_DETAILS, details);
export const setValidationSummary = (summary: BelValidationSummary) =>
  action(BelActionTypes.SET_VALIDATION_SUMMARY, summary);
export const setBufferDistance = (distance: number) =>
  action(BelActionTypes.SET_BUFFER_DISTANCE, distance);
export const setMaxSplitArea = (areaKm: number) =>
  action(BelActionTypes.SET_MAX_SPLIT_AREA, areaKm);
export const setApplyBuffer = (apply: boolean) =>
  action(BelActionTypes.SET_APPLY_BUFFER, apply);
export const setApplySplit = (apply: boolean) =>
  action(BelActionTypes.SET_APPLY_SPLIT, apply);
export const validateGeometrySettingsStart = (payload?: {
  pendingStepAdvance?: boolean;
}) => action(BelActionTypes.VALIDATE_GEOMETRY_SETTINGS_START, payload ?? {});
export const validateGeometrySettingsSuccess = (payload: {
  canProceed: boolean;
  requiresSplit: boolean;
  violations: BelGeometryViolation[];
}) => action(BelActionTypes.VALIDATE_GEOMETRY_SETTINGS_SUCCESS, payload);
export const validateGeometrySettingsFailure = (error: string) =>
  action(BelActionTypes.VALIDATE_GEOMETRY_SETTINGS_FAILURE, error);
export const clearGeometrySettingsValidation = () =>
  action(BelActionTypes.CLEAR_GEOMETRY_SETTINGS_VALIDATION);
export const applyGeometryPipelineStart = () =>
  action(BelActionTypes.APPLY_GEOMETRY_PIPELINE_START);
export const applyGeometryPipelineSuccess = () =>
  action(BelActionTypes.APPLY_GEOMETRY_PIPELINE_SUCCESS);
export const applyGeometryPipelineFailure = (error: string) =>
  action(BelActionTypes.APPLY_GEOMETRY_PIPELINE_FAILURE, error);
export const clearGeometryPipelineError = () =>
  action(BelActionTypes.CLEAR_GEOMETRY_PIPELINE_ERROR);
export const resetBel = () => action(BelActionTypes.RESET_BEL);
